import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";

const navItems = [
  { id: "presence", label: "About", testId: "nav-philosophy" },
  { id: "roles", label: "Who it helps", testId: "nav-science" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleNav = (sectionId) => {
    setMobileOpen(false);
    scrollToSection(sectionId);
  };

  const handleWaitlist = () => {
    setMobileOpen(false);
    scrollToSection("waitlist", { focusEmail: true });
  };

  return (
    <motion.header
      data-testid="header"
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-teamie-bg/70 border-b border-teamie-border/40 shadow-sm"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex items-center justify-between h-16 sm:h-20">
          <button
            type="button"
            data-testid="header-logo"
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="font-heading text-2xl sm:text-3xl font-bold text-teamie-text tracking-tight hover:text-teamie-accent transition-colors duration-300"
          >
            teamie.
          </button>

          <nav className="hidden md:flex items-center gap-10" data-testid="desktop-nav">
            {navItems.map((item) => (
              <button
                key={item.id}
                type="button"
                data-testid={item.testId}
                onClick={() => handleNav(item.id)}
                className="font-mono text-xs tracking-[0.15em] uppercase text-teamie-text-secondary hover:text-teamie-accent transition-colors duration-300"
              >
                {item.label}
              </button>
            ))}
            <button
              type="button"
              data-testid="nav-waitlist-cta"
              onClick={handleWaitlist}
              className="px-6 py-2.5 rounded-full border border-teamie-text text-teamie-text text-sm font-body font-medium hover:bg-teamie-accent hover:text-teamie-bg hover:border-teamie-accent transition-colors duration-300"
            >
              Join Waitlist
            </button>
          </nav>

          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden p-2 text-teamie-text"
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X size={22} strokeWidth={1.5} /> : <Menu size={22} strokeWidth={1.5} />}
          </button>
        </div>

        {mobileOpen && (
          <motion.nav
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden pb-6 flex flex-col gap-4"
            data-testid="mobile-nav"
          >
            {navItems.map((item) => (
              <button
                key={item.id}
                type="button"
                data-testid={`mobile-${item.testId}`}
                onClick={() => handleNav(item.id)}
                className="font-mono text-xs tracking-[0.15em] uppercase text-teamie-text-secondary hover:text-teamie-accent text-left py-2"
              >
                {item.label}
              </button>
            ))}
            <button
              type="button"
              data-testid="mobile-nav-waitlist"
              onClick={handleWaitlist}
              className="px-6 py-2.5 rounded-full border border-teamie-text text-teamie-text text-sm font-body font-medium hover:bg-teamie-accent hover:text-teamie-bg hover:border-teamie-accent transition-colors duration-300 w-fit"
            >
              Join Waitlist
            </button>
          </motion.nav>
        )}
      </div>
    </motion.header>
  );
}
